import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { sendAuthUserOTP,authVerifyOtp,authChangePassword } from '../services/authRequests';
import { toast } from 'react-toastify';

const ChangePasswordModal = ({ isOpen, onClose, email }) => {
  // Start at Step 1: Request OTP
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [resetLoading, setResetLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false); // Indicates OTP is being sent

  //console.log("Rendering ForgotPasswordModal with Email:", email);

  // Start or reset countdown when in OTP step (Step 2)
  useEffect(() => {
    let timerId;
    if (step === 2) {
      setResendTimer(60);
      timerId = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(timerId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerId);
  }, [step]);

  if (!isOpen) return null;

  // Function to send OTP when requested by user (Step 1 or Resend)
  const sendOtp = async () => {
    if (!email) {
      toast.error("Email is required.");
      return;
    }
    setSendingOtp(true);
    try {
      await sendAuthUserOTP();
     // console.log("OTP sent to:", email);
      // After sending OTP, move to OTP verification step
      setStep(2);
    } catch (error) {
      console.error("Error sending OTP:", error.response?.data || error.message);
      toast.error("Error sending OTP. Please try again.");
    }
    setSendingOtp(false);
  };

  // Allow only numeric input for OTP and auto-focus next input
  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (value && isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && e.target.nextSibling) {
      e.target.nextSibling.focus();
    }
  };

  const isOtpComplete = otp.every(val => val !== '');

  // Handle OTP verification: combine OTP digits, verify, and store token
  const handleOtpVerify = async () => {
    if (!isOtpComplete) {
      toast.info("Please enter the complete OTP.");
      return;
    }
    const otpValue = otp.join('');
    setOtpVerifying(true);
    try {
      const response = await authVerifyOtp(otpValue);
      //console.log("OTP Verification successful:", response.token);
      toast.success("OTP Verification successful.");
      setOtpToken(response.token);
      // Move to change password step
      setStep(3);
    } catch (error) {
      console.error("OTP Verification failed:", error.response?.data || error.message);
      let message = error.response?.data || "An error occurred.";
      toast.error(message);
    }
    setOtpVerifying(false);
  };

  // Handle Resend OTP functionality
  const handleResendOtp = async () => {
    try {
      await sendAuthUserOTP();
      setResendTimer(60); // reset timer
      toast.info("OTP Resent successfully.");
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("Error resending OTP. Please try again.");
    }
  };

  // STEP 1: Request OTP (show button to send OTP)
  const renderStep1 = () => (
    <div className="modal-dialog">
      <div 
        className="modal-content p-4 border border-gray-100 hover-border-main-600 transition-1"
        style={{ borderRadius: "0" }}
      >
        <button 
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            fontSize: "1.25rem",
            background: "transparent",
            border: "none",
            cursor: "pointer"
          }}
          aria-label="Close"
        >
          ×
        </button>
        <div className="modal-header border-0 p-0 mb-4">
          <h6 className="modal-title text-sm text-neutral-900 fw-semibold">
            Request OTP
          </h6>
        </div>
        <div className="modal-body p-0">
          <p className="text-sm text-gray-600 mb-4">
            Click the button below to request an OTP to change your password for <strong>{email}</strong>.
          </p>
        </div>
        <div className="modal-footer border-0 p-0">
          <button
            className="btn btn-main btn-sm"
            onClick={sendOtp}
            disabled={sendingOtp}
          >
            {sendingOtp ? "Sending OTP..." : "Send OTP"}
          </button>
        </div>
      </div>
    </div>
  );

  // STEP 2: Enter OTP
  const renderStep2 = () => (
    <div className="modal-dialog">
      <div 
        className="modal-content p-4 border border-gray-100 hover-border-main-600 transition-1"
        style={{ borderRadius: "0" }}
      >
        <button 
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            fontSize: "1.25rem",
            background: "transparent",
            border: "none",
            cursor: "pointer"
          }}
          aria-label="Close"
        >
          ×
        </button>
        <div className="modal-header border-0 p-0 mb-4">
          <h6 className="modal-title text-sm text-neutral-900 fw-semibold">
            Enter verification code
          </h6>
        </div>
        <div className="modal-body p-0">
          <p className="text-sm text-gray-600 mb-4">
            We have sent a verification code to <strong>{email}</strong>. Please enter it below.
          </p>
          <div className="d-flex justify-content-center mb-4">
            {otp.map((data, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                value={data}
                onChange={(e) => handleOtpChange(e, index)}
                className="text-center"
                style={{
                  width: "50px",
                  height: "50px",
                  fontSize: "1.25rem",
                  textAlign: "center",
                  border: "1px solid #ccc",
                  borderRadius: "0",
                  margin: "0 4px"
                }}
              />
            ))}
          </div>
          {/* Resend OTP Section */}
          <div className="text-center mb-4">
            {resendTimer > 0 ? (
              <p className="text-sm text-gray-500">Resend OTP in {resendTimer} seconds</p>
            ) : (
              <button
                className="text-sm text-main-600 underline"
                onClick={handleResendOtp}
              >
                Resend OTP
              </button>
            )}
          </div>
        </div>
        <div className="modal-footer border-0 p-0">
          <button
            className={`btn btn-main btn-sm ${!isOtpComplete ? 'disabled' : ''}`}
            onClick={handleOtpVerify}
            disabled={!isOtpComplete || otpVerifying}
          >
            {otpVerifying ? "Verifying..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );

  // STEP 3: Change Password (token available for later use)
  const renderStep3 = () => (
    <div className="modal-dialog">
      <div 
        className="modal-content p-4 border border-gray-100 hover-border-main-600 transition-1"
        style={{ borderRadius: "0" }}
      >
        <button 
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            fontSize: "1.25rem",
            background: "transparent",
            border: "none",
            cursor: "pointer"
          }}
          aria-label="Close"
        >
          ×
        </button>
        <div className="modal-header border-0 p-0 mb-4">
          <h6 className="modal-title text-sm text-neutral-900 fw-semibold">
            Change Password
          </h6>
        </div>
        <div className="modal-body mb-4">
          <input
            type="password"
            className="common-input-two text-sm mb-4"
            placeholder="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <input
            type="password"
            className="common-input-two text-sm mb-4"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>
        <div className="modal-footer border-0 p-0">
          <button
            className="btn btn-main btn-sm"
            onClick={async () => {
              if (!password || password !== confirmPassword) {
                toast.error("Passwords do not match.");
                return;
              }
              setResetLoading(true);
              try {
                const response = await authChangePassword(otpToken, password);
                //console.log("Password reset successful:", response);
                toast.success("Password reset successful.");
                onClose();
                window.location.reload(); // Reload the page to reflect changes
              } catch (error) {
                console.error("Password reset failed:", error.response?.data || error.message);
                toast.error("Password reset failed. Please try again.");
              }
              setResetLoading(false);
            }}
            disabled={resetLoading}
          >
            {resetLoading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>,
    document.getElementById("modal-root")
  );
};

export default ChangePasswordModal;
